import { fetchJson } from './client.js';

export const getWorkerRetention = async (id, environment, options = {}) =>
  (await fetchJson(`${environmentPath(id, environment)}/retention`, { auth: 'always', ...options })).data;

export const workerRetentionAction = async (id, environment, action) =>
  (await fetchJson(`${environmentPath(id, environment)}/retention/actions`, { auth: 'always', method: 'POST', body: { action } })).data;

export const setWorkerRetentionRecoveryPreference = async (id, environment, autoResumeAfterFunding) =>
  (await fetchJson(`${environmentPath(id, environment)}/retention/recovery-preference`, { auth: 'always', method: 'POST', body: { autoResumeAfterFunding } })).data;

export const getWorkerRetentionQuote = async (id, environment, resourceType) =>
  (await fetchJson(`${environmentPath(id, environment)}/retention/quote/${encodeURIComponent(resourceType)}`, { auth: 'always' })).data;

export const acceptWorkerRetention = async (id, environment, quote) =>
  (await fetchJson(`${environmentPath(id, environment)}/retention/accept`, {
    auth: 'always', method: 'POST', body: {
      policyVersion: quote.policyVersion, priceVersion: quote.priceVersion, automaticDeletionAccepted: true,
    },
  })).data;

export const getWorkerRunningReserve = async (id, environment) =>
  (await fetchJson(`${environmentPath(id, environment)}/billing/running-reserve`, { auth: 'always' })).data;

export const getWorkerRunningReserveQuote = async (id, environment) =>
  (await fetchJson(`${environmentPath(id, environment)}/billing/running-reserve/quote?additionalResource=true`, { auth: 'always' })).data;

export const listWorkers = async ({ includeDeleted = false } = {}) =>
  (await fetchJson(`/v1/workers${includeDeleted ? '?includeDeleted=true' : ''}`, { auth: 'always' })).data;

export const getWorker = async (id) =>
  (await fetchJson(`/v1/workers/${encodeURIComponent(id)}`, { auth: 'always' }))
    .data;

export const createWorker = async (input) =>
  (
    await fetchJson('/v1/workers', {
      method: 'POST',
      auth: 'always',
      body: input,
    })
  ).data;

export const uploadArtifact = async (id, artifact, idempotencyKey) =>
  (
    await fetchJson(`/v1/workers/${encodeURIComponent(id)}/artifacts`, {
      method: 'POST',
      auth: 'always',
      body:
        typeof artifact === 'string'
          ? { moduleCode: artifact, idempotencyKey }
          : { bundle: artifact, idempotencyKey },
    })
  ).data;

export const deployWorker = async (id, input) =>
  (
    await fetchJson(`/v1/workers/${encodeURIComponent(id)}/deployments`, {
      method: 'POST',
      auth: 'always',
      body: input,
    })
  ).data;

export const rollbackWorker = async (
  id,
  environment,
  deploymentId,
  idempotencyKey,
) =>
  (
    await fetchJson(
      `/v1/workers/${encodeURIComponent(id)}/environments/${encodeURIComponent(environment)}/rollback`,
      {
        method: 'POST',
        auth: 'always',
        body: { deploymentId, idempotencyKey },
      },
    )
  ).data;

export const updateBudget = async (id, environment, dailyBudgetUsd) =>
  (
    await fetchJson(
      `/v1/workers/${encodeURIComponent(id)}/environments/${environment}`,
      {
        method: 'PATCH',
        auth: 'always',
        body: { dailyBudgetUsd },
      },
    )
  ).data;

export const getProviderStatus = async () =>
  (await fetchJson('/v1/workers/provider/status', { auth: 'always' })).data;

export const getProviderCapabilities = async () =>
  (await fetchJson('/v1/workers/provider/capabilities', { auth: 'always' }))
    .data;

export const getBuildProviderStatus = async () =>
  (await fetchJson('/v1/workers/build-provider/status', { auth: 'always' }))
    .data;

export const getArtifactProviderStatus = async () =>
  (await fetchJson('/v1/workers/artifact-provider/status', { auth: 'always' }))
    .data;

export const getBillingStatus = async () =>
  (await fetchJson('/v1/workers/billing/status', { auth: 'always' })).data;

export const getUsage = async (id, environment) =>
  (
    await fetchJson(
      environment
        ? `${environmentPath(id, environment)}/usage`
        : `/v1/workers/${encodeURIComponent(id)}/usage`,
      { auth: 'always' },
    )
  ).data;

export const retryDomain = async (id, domainId) =>
  (
    await fetchJson(
      `/v1/workers/${encodeURIComponent(id)}/domains/${encodeURIComponent(domainId)}/retry`,
      { method: 'POST', auth: 'always' },
    )
  ).data;

const environmentPath = (id, environment) =>
  `/v1/workers/${encodeURIComponent(id)}/environments/${encodeURIComponent(environment)}`;

export const getMeteredUsage = async (id, environment, { signal } = {}) =>
  (await fetchJson(`${environmentPath(id, environment)}/metered-usage`, {
    auth: 'always', signal,
  })).data;

const billingPath = (id, environment, family) =>
  `${environmentPath(id, environment)}/billing/${family}`;

const withQuery = (path, query = {}) => {
  const search = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, String(value));
    }
  });
  const suffix = search.toString();
  return suffix ? `${path}?${suffix}` : path;
};

const getBillingFamily = async (
  id,
  environment,
  family,
  { query, signal } = {},
) =>
  (
    await fetchJson(withQuery(billingPath(id, environment, family), query), {
      auth: 'always',
      signal,
    })
  ).data;

export const getBillingPrices = (id, environment, options) =>
  getBillingFamily(id, environment, 'prices', options);

export const getBillingOverview = (id, environment, options) =>
  getBillingFamily(id, environment, 'overview', options);

export const getBillingUsage = (id, environment, query = {}, options = {}) =>
  getBillingFamily(id, environment, 'usage', {
    ...options,
    query,
  });

export const getBillingLedger = (id, environment, query = {}, options = {}) =>
  getBillingFamily(id, environment, 'ledger', {
    ...options,
    query,
  });

export const getBillingForecast = (id, environment, options) =>
  getBillingFamily(id, environment, 'forecast', options);

export const getBillingRisk = (id, environment, options) =>
  getBillingFamily(id, environment, 'risk', options);

export const getBillingLifecycle = (id, environment, options) =>
  getBillingFamily(id, environment, 'lifecycle', options);

export const listResources = async (id, environment) =>
  (
    await fetchJson(`${environmentPath(id, environment)}/resources`, {
      auth: 'always',
    })
  ).data;

export const createResource = async (id, environment, input) =>
  (
    await fetchJson(`${environmentPath(id, environment)}/resources`, {
      method: 'POST',
      auth: 'always',
      body: input,
    })
  ).data;

export const deleteResource = async (id, environment, resourceId) =>
  (
    await fetchJson(
      `${environmentPath(id, environment)}/resources/${encodeURIComponent(resourceId)}`,
      { method: 'DELETE', auth: 'always' },
    )
  ).data;

export const listSecrets = async (id, environment) =>
  (
    await fetchJson(`${environmentPath(id, environment)}/secrets`, {
      auth: 'always',
    })
  ).data;

export const putSecret = async (id, environment, bindingName, value) =>
  (
    await fetchJson(
      `${environmentPath(id, environment)}/secrets/${encodeURIComponent(bindingName)}`,
      { method: 'PUT', auth: 'always', body: { value } },
    )
  ).data;

export const deleteSecret = async (id, environment, bindingName) =>
  (
    await fetchJson(
      `${environmentPath(id, environment)}/secrets/${encodeURIComponent(bindingName)}`,
      { method: 'DELETE', auth: 'always' },
    )
  ).data;

export const listInvocations = async (id, environment) =>
  (
    await fetchJson(`${environmentPath(id, environment)}/invocations`, {
      auth: 'always',
    })
  ).data;

export const listRuntimeLogs = async (id, environment) =>
  (
    await fetchJson(`${environmentPath(id, environment)}/runtime-logs`, {
      auth: 'always',
    })
  ).data;

export const listSchedules = async (id) =>
  (
    await fetchJson(`/v1/workers/${encodeURIComponent(id)}/schedules`, {
      auth: 'always',
    })
  ).data;

export const createSchedule = async (id, input) =>
  (
    await fetchJson(`/v1/workers/${encodeURIComponent(id)}/schedules`, {
      method: 'POST',
      auth: 'always',
      body: input,
    })
  ).data;

export const updateSchedule = async (id, scheduleId, input) =>
  (
    await fetchJson(
      `/v1/workers/${encodeURIComponent(id)}/schedules/${encodeURIComponent(scheduleId)}`,
      {
        method: 'PATCH',
        auth: 'always',
        body: input,
      },
    )
  ).data;

export const deleteSchedule = async (id, scheduleId) =>
  (
    await fetchJson(
      `/v1/workers/${encodeURIComponent(id)}/schedules/${encodeURIComponent(scheduleId)}`,
      {
        method: 'DELETE',
        auth: 'always',
      },
    )
  ).data;

export const listScheduleRuns = async (id, scheduleId) =>
  (
    await fetchJson(
      `/v1/workers/${encodeURIComponent(id)}/schedules/${encodeURIComponent(scheduleId)}/runs`,
      {
        auth: 'always',
      },
    )
  ).data;

export const runScheduleNow = async (id, scheduleId) =>
  (
    await fetchJson(
      `/v1/workers/${encodeURIComponent(id)}/schedules/${encodeURIComponent(scheduleId)}/run`,
      {
        method: 'POST',
        auth: 'always',
      },
    )
  ).data;
